import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { isBirthCheckOk } from 'validators/Validator';
import { FormErrors } from 'validators/UserRegister3Validator1';
import { ActionBase } from 'store/ActionBase';
import { storeRegisterUserData1Generator } from 'store/register-user/RegisterUserData';
import { SiteDisplayItemsExtend } from 'hooks/UseSiteDisplayItems';
import { callCheckGakkenIdDuplicateApi } from 'requests/RequestUser';
import { RegisterUserForm } from 'components/templates/UserRegister3Template1';
import { RoutePath } from 'consts/RoutePath';
import { HttpStatus } from 'consts/HttpStatus';
import { ReactError } from 'consts/ReactError';
import { CONFIRM_ERROR_PASSWORD, HARD_TO_GUESS_PASSWORD } from 'utils/messages';

/**
 * 新規会員登録3-1から3-2へ遷移する 次へ 処理のカスタムフック
 * フォームバリデーション -> 次へ実処理 を段階的に分けている
 * useStateが非同期なのでuseEffectを使って段階的にしないとエラーをキャッチすることができないため
 */
export const useNext = (
	formErrors: FormErrors,
	setFormErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
	getErrorMessage: Function,
	formData: RegisterUserForm,
	dispatch: React.Dispatch<ActionBase>,
	displayItems: SiteDisplayItemsExtend,
	actionError: string,
	setActionError: React.Dispatch<React.SetStateAction<string>>,
	setProcessLoading: React.Dispatch<React.SetStateAction<boolean>>,
) => {
	const history = useHistory();
	// フォームバリデーション
	const [isFormValidationFinished, setIsFormValidationFinished] = useState(false);
	const next = async () => {
		setProcessLoading(true);

		Object.keys(formErrors).forEach((k) => {
			setFormErrors((prev) => ({ ...prev, [k]: getErrorMessage(k, formData[k], displayItems[k]) }));
		});
		const isPasswordsMatched = formData.pass === formData.password_confirm;
		const hardToGuessPassword = formData.gid.toLowerCase() !== formData.pass.toLowerCase();

		if (!isPasswordsMatched) {
			setFormErrors((prev) => ({
				...prev,
				password_confirm: CONFIRM_ERROR_PASSWORD,
			}));
		}
		// GIDと同じパスワードを設定できない
		if (!hardToGuessPassword) {
			setFormErrors((prev) => ({
				...prev,
				pass: HARD_TO_GUESS_PASSWORD,
			}));
		}

		// 年月日どれかが入力されている場合、生年月日の正当性チェック
		if (formData.birthYear || formData.birthMonth || formData.birthDate) {
			const isOk = isBirthCheckOk(formData.birthYear, formData.birthMonth, formData.birthDate);
			if (!isOk) {
				setFormErrors((prev) => ({ ...prev, birthYear: '正しい生年月日を入力してください。' }));
			}
		}

		setIsFormValidationFinished(true);
	};

	// 学研ID重複確認
	const [isGidDuplicateValidationFinished, setIsGidDuplicateValidationFinished] = useState(false);
	useEffect(() => {
		if (!isFormValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '') || actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setProcessLoading(false);
			return;
		}

		const execute = async () => {
			setActionError('');
			const checkGidResult = await callCheckGakkenIdDuplicateApi({
				gid: formData.gid,
			});
			if (checkGidResult.status !== HttpStatus.OK) {
				setActionError(ReactError.E9999);
				history.push(RoutePath.ERROR);
				return;
			}
			if (checkGidResult.data.exist) {
				setFormErrors((prev) => ({ ...prev, gid: ReactError.E0005 }));
			}
			setIsGidDuplicateValidationFinished(true);
		};
		execute();
	}, [isFormValidationFinished]);

	// 次へ実処理
	useEffect(() => {
		if (!isGidDuplicateValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '') || actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setIsGidDuplicateValidationFinished(false);
			setProcessLoading(false);
			return;
		}

		// ストアに格納
		storeRegisterUserData1Generator(dispatch, formData);

		// 次のページへ
		history.push(displayItems.isSkip3_2 ? RoutePath.USER_REGISTER_4 : RoutePath.USER_REGISTER_3_2, displayItems);
	}, [isGidDuplicateValidationFinished]);

	return {
		next,
	};
};
